import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useUser } from "util/context/user_context";
import { getTotalRating } from "firebase_/firebase_function/expert_function";
import {
  getAllMatchingList,
  getAllRequestList,
  getMyMatchingList,
} from "firebase_/firebase_function/expert_match";
import AdBannerSlot from "components/main/ad_banner_slot";
import NewCommunityStoryM from "components/main/new_community_story_m";

const C = {
  green: "#059669",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  bg: "#F7FAF7",
  amber50: "#FFFBEB",
  amber100: "#FEF3C7",
  amber800: "#92400E",
};

function StatusChip({ status }) {
  const map = {
    0: { label: "매칭중", bg: C.amber100, fg: C.amber800, dot: "#F59E0B" },
    1: { label: "매칭완료", bg: "#DBEAFE", fg: "#1E40AF", dot: "#3B82F6" },
    2: { label: "완료", bg: C.greenBg, fg: C.greenDark, dot: C.green },
  };
  const s = map[status] ?? map[0];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        borderRadius: 9999,
        background: s.bg,
        color: s.fg,
        fontSize: 10,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 4, height: 4, borderRadius: 9999, background: s.dot }} />
      {s.label}
    </span>
  );
}

const categoryEmoji = (name = "") => {
  const s = String(name);
  if (s.includes("트랙터")) return "🚜";
  if (s.includes("콤바인")) return "🌾";
  if (s.includes("제어기") || s.includes("전자") || s.includes("전기")) return "⚡";
  if (s.includes("엔진") || s.includes("모터")) return "⚙️";
  if (s.includes("배터리")) return "🔋";
  if (s.includes("오일") || s.includes("연료")) return "🛢️";
  if (s.includes("이앙기") || s.includes("파종")) return "🌱";
  return "🔧";
};

const relativeTime = (dateStr) => {
  if (!dateStr) return "";
  const m = moment(dateStr);
  if (!m.isValid()) return "";
  const diffMin = moment().diff(m, "minutes");
  if (diffMin < 1) return "방금 전";
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHr = moment().diff(m, "hours");
  if (diffHr < 24) return `${diffHr}시간 전`;
  const diffDay = moment().diff(m, "days");
  if (diffDay < 7) return `${diffDay}일 전`;
  return m.format("YYYY.MM.DD");
};

function MiniStat({ icon, label, value, accent, accentBg }) {
  return (
    <div
      style={{
        flex: 1,
        padding: "12px 14px",
        background: "#fff",
        borderRadius: 12,
        border: `1px solid ${C.line}`,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        minWidth: 0,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: accentBg,
          color: accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
        }}
      >
        {icon}
      </div>
      <div style={{ fontSize: 11, color: C.sub, fontWeight: 500 }}>{label}</div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 800,
          color: C.ink,
          letterSpacing: "-0.02em",
          lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function MatchRowMobile({ item, onClick }) {
  const names = item?.categoryListName ?? [];
  const isEtc = names[0] === "기타";
  const primary = isEtc ? "기타" : names[0] || "기타";
  const secondary = isEtc ? "" : [names[1], names[2]].filter(Boolean).join(" · ");
  const emoji = categoryEmoji(primary);
  const images = Array.isArray(item?.imageUrls) ? item.imageUrls : [];
  const firstImage = images[0];

  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        gap: 12,
        padding: 12,
        borderRadius: 10,
        cursor: "pointer",
        alignItems: "center",
        background: "#FAFAFA",
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: firstImage ? "#F3F4F6" : C.greenBg,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22,
          overflow: "hidden",
        }}
      >
        {firstImage ? (
          <img src={firstImage} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <span>{emoji}</span>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: C.ink,
            letterSpacing: "-0.01em",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {primary}
          {secondary && <span style={{ color: C.sub, fontWeight: 500 }}> · {secondary}</span>}
        </div>
        <div style={{ fontSize: 11, color: C.sub }}>{relativeTime(item.createDate)}</div>
      </div>
      <StatusChip status={item.status} />
    </div>
  );
}

export default function ExpertMainMobile() {
  const navigate = useNavigate();
  const { userData } = useUser();
  const [averageRating, setAverageRating] = useState(0);
  const [totalList, setTotalList] = useState([]);
  const [requestList, setRequestList] = useState([]);
  const [matchList, setMatchList] = useState([]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [total, request, match, rating] = await Promise.all([
        getAllMatchingList(),
        getAllRequestList(),
        getMyMatchingList(userData["id"]),
        getTotalRating(userData["id"]),
      ]);
      setTotalList(total || []);
      setRequestList(request || []);
      setMatchList(match || []);
      setAverageRating(rating || 0);
    } catch {
      /* noop */
    }
  };

  const point = Number(userData?.point ?? 0).toLocaleString();
  const recentMatches = useMemo(() => (matchList || []).slice(0, 3), [matchList]);

  return (
    <div
      style={{
        fontFamily:
          'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
        padding: "16px 16px 32px",
        background: C.bg,
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      {/* Welcome hero */}
      <div
        style={{
          background: "linear-gradient(135deg, #0F766E 0%, #064E3B 100%)",
          borderRadius: 16,
          padding: 18,
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "3px 10px",
            borderRadius: 9999,
            background: "rgba(255,255,255,0.18)",
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          🛠 수리기사 대시보드
        </div>
        <div
          style={{
            marginTop: 10,
            fontSize: 19,
            fontWeight: 800,
            letterSpacing: "-0.02em",
            lineHeight: 1.3,
          }}
        >
          {userData?.name ?? "기사"}님, 오늘도 수고하세요 👋
        </div>
        <div style={{ marginTop: 4, fontSize: 12, color: "rgba(255,255,255,0.85)" }}>
          새로운 수리 요청과 매칭을 한눈에 확인하세요.
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button
            type="button"
            onClick={() => navigate("/matching")}
            style={{
              flex: 1,
              padding: "9px 0",
              borderRadius: 9,
              background: "#fff",
              color: C.greenDark,
              fontSize: 13,
              fontWeight: 700,
              border: "none",
              cursor: "pointer",
            }}
          >
            요청 보기 →
          </button>
          <button
            type="button"
            onClick={() => navigate("/matching_done")}
            style={{
              flex: 1,
              padding: "9px 0",
              borderRadius: 9,
              background: "rgba(255,255,255,0.16)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              border: "1px solid rgba(255,255,255,0.25)",
              cursor: "pointer",
            }}
          >
            내 매칭
          </button>
        </div>
      </div>

      {/* Point card */}
      <div
        style={{
          background: "#fff",
          border: `1px solid ${C.line}`,
          borderRadius: 14,
          padding: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 24,
                height: 24,
                borderRadius: 7,
                background: C.amber50,
                color: C.amber800,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
              }}
            >
              💰
            </span>
            <span style={{ fontSize: 11, color: C.sub, fontWeight: 600 }}>현재 포인트</span>
          </div>
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: "-0.02em",
              lineHeight: 1,
            }}
          >
            {point}
            <span style={{ fontSize: 13, color: C.sub, fontWeight: 700, marginLeft: 3 }}>P</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/addpoint")}
          style={{
            padding: "8px 14px",
            borderRadius: 9,
            background: C.greenDark,
            color: "#fff",
            fontSize: 12,
            fontWeight: 700,
            border: "none",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          충전 +
        </button>
      </div>

      {/* Banner */}
      <AdBannerSlot />

      {/* Stats 2x2 */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <MiniStat
          icon="⭐"
          label="평균 평점"
          value={Number(averageRating || 0).toFixed(1)}
          accent="#B45309"
          accentBg="#FEF3C7"
        />
        <MiniStat
          icon="📋"
          label="전체 등록"
          value={totalList.length}
          accent={C.ink}
          accentBg="#F3F4F6"
        />
        <MiniStat
          icon="🤝"
          label="대기 요청"
          value={requestList.length}
          accent={C.amber800}
          accentBg={C.amber50}
        />
        <MiniStat
          icon="✅"
          label="나의 매칭"
          value={matchList.length}
          accent={C.greenDark}
          accentBg={C.greenBg}
        />
      </div>

      {/* 매칭 목록 */}
      <section
        style={{
          background: "#fff",
          border: `1px solid ${C.line}`,
          borderRadius: 14,
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2
            style={{
              fontSize: 16,
              fontWeight: 800,
              color: C.ink,
              margin: 0,
              letterSpacing: "-0.01em",
            }}
          >
            내 매칭 목록
          </h2>
          <button
            type="button"
            onClick={() => navigate("/matching_done")}
            style={{
              background: "transparent",
              border: "none",
              color: C.greenDark,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            전체보기 →
          </button>
        </div>

        {recentMatches.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {recentMatches.map((item, index) => (
              <MatchRowMobile
                key={item?.docId || index}
                item={item}
                onClick={() => navigate(`/matching_done/${item.docId}`)}
              />
            ))}
          </div>
        ) : (
          <div
            style={{
              padding: "24px 12px",
              textAlign: "center",
              color: C.sub,
              fontSize: 13,
              background: "#FAFAFA",
              borderRadius: 10,
            }}
          >
            아직 매칭된 요청이 없어요.
          </div>
        )}
      </section>

      {/* 커뮤니티 최신 글 */}
      <section
        style={{
          background: "#fff",
          border: `1px solid ${C.line}`,
          borderRadius: 14,
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2
            style={{
              fontSize: 16,
              fontWeight: 800,
              color: C.ink,
              margin: 0,
              letterSpacing: "-0.01em",
            }}
          >
            커뮤니티 최신 글
          </h2>
          <button
            type="button"
            onClick={() => navigate("/community")}
            style={{
              background: "transparent",
              border: "none",
              color: C.greenDark,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            더보기 →
          </button>
        </div>
        <NewCommunityStoryM />
      </section>
    </div>
  );
}
